import { useEffect } from 'react';
import dynamic from 'next/dynamic';

import { useI18n } from 'context/i18n';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { isBlank } from 'utils/StringUtils';
import { useLocale } from 'hooks';
import { useProductSheet } from 'context/product-sheet';
import { getDomainValue } from 'utils/domain';

import { Disclosure, DisclosureGroup, DisclosureHeader, DisclosurePanel } from 'components/ui/disclosure';
import { Tooltip } from 'components/ui/tooltip';
import { LabAddIcon } from 'components/camperlab/icons/add';

const GuarantyInfo = dynamic(() => import('components/guarantyInfo'));

const measureImgSrc = getDomainValue({
  camper: 'https://www.camper.com/html/CAMPERLAB/GAFAS_SOL',
  nnormal: 'https://www.nnormal.com/html/CAMPERLAB/GAFAS_SOL',
  camperlab: 'https://www.camperlab.com/html/CAMPERLAB/GAFAS_SOL',
});

export function LabProductInfoSection() {
  const { t, giftCardList } = useI18n();
  const { country: market } = useLocale();
  const { productSheet } = useProductSheet();
  const isGiftCard = giftCardList.includes(productSheet.code);
  useEffect(() => {
    window.handleKlarnaOpen = () => {
      dataLayerHandleEvent({
        event: 'item_info',
        event_category: 'product detail',
        event_detail_1: 'installment payment',
        event_detail_2: '',
      });
    };
  }, []);

  const handlerDataOpenAccordion = (key: string) => {
    const keyMapper = {
      // 1: 'description',
      2: 'features',
      3: 'product care',
    };

    if (key) {
      dataLayerHandleEvent({
        event: 'item_info',
        event_category: 'product detail',
        event_detail_1: keyMapper[key],
        event_detail_2: '',
      });
    }
  };

  return (
    <div id={'product-info'} className="text-sm lg:text-xs">
      {!isGiftCard ?
        <>
          {market === 'AU' && <span>{t('listado.productos', 'only.on.camper.com', '***Only on camper.com')}</span>}
          <DisclosureGroup
            defaultExpandedKeys={['warranty']}
            onExpandedChange={(key) => {
              handlerDataOpenAccordion(key as unknown as string);
            }}
          >
            {productSheet.guarantyInfoArray && productSheet.guarantyInfoArray.filter(Boolean).length > 0 && (
              <Disclosure id="warranty">
                <DisclosureHeader level={2}>{t('ficha.producto', 'warranty')}</DisclosureHeader>
                <DisclosurePanel className="text-sm lg:text-xs">
                  <ul className="mb-0">
                    {productSheet?.relevantInfo && (
                      <>
                        {productSheet.relevantInfo.map((item, index) => (
                          <li key={`guaranty-${index}`} dangerouslySetInnerHTML={{ __html: item }} />
                        ))}
                      </>
                    )}
                    {productSheet.isRecrafted && !productSheet.isRoku && (
                      <li className="my-0">{t('listado.productos', 'text.info.recrafted', 'Your order will be delivered in 4 - 6 weeks.')}</li>
                    )}
                    {productSheet.isGoretex3d && <li>{t('listado.productos', 'text.info.goretex3d')}</li>}
                    {productSheet.isObraDeArte && <li>{t('listado.productos', 'text.info.obras.de.arte')}</li>}
                    {productSheet.isObraDeArteSonFortesa && <li>{t('listado.productos', 'text.info.obras.de.arte.son.fortesa')}</li>}
                    {productSheet.recycled ?
                      <li
                        className="my-0"
                        data-info={`text.info.recycled${productSheet.recycledStatus === 'good' ? '.good' : ''}`}
                        dangerouslySetInnerHTML={{
                          __html: t('listado.productos', `text.info.recycled${productSheet.recycledStatus === 'good' ? '.good' : ''}`, '<b>Recycled</b>'),
                        }}
                      ></li>
                    : productSheet.guarantyInfoArray.map((info, index) => <GuarantyInfo key={`guarantee-text-${index}`} infoText={info} />)}
                  </ul>
                </DisclosurePanel>
              </Disclosure>
            )}
            {productSheet.features ?
              <Disclosure id="features">
                <DisclosureHeader data-id="features" level={2}>
                  {t('ficha.producto', 'caracteristicas', 'Features')}
                </DisclosureHeader>
                <DisclosurePanel className="text-sm lg:text-xs">
                  {productSheet.features?.map((featureItem, index) => {
                    const { hasModal, value } = featureItem;
                    const valueHtml = value === undefined ? '' : value;
                    let featureContent = (
                      <div key={`feature-${index}`}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: `${featureItem.name}${valueHtml}`,
                          }}
                        />
                      </div>
                    );

                    if (hasModal) {
                      featureContent = (
                        <div key={`feature-${index}`}>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: `${featureItem.name}${valueHtml}`,
                            }}
                          />
                          <Tooltip placement="top" message={<span dangerouslySetInnerHTML={{ __html: featureItem.modalText }}></span>}>
                            <LabAddIcon />
                          </Tooltip>
                        </div>
                      );
                    }

                    return featureContent;
                  })}
                </DisclosurePanel>
              </Disclosure>
            : null}
            {/* <Disclosure id="description">
              <DisclosureHeader level={2}>{t('ficha.producto', 'description', 'Description')}</DisclosureHeader>
              <DisclosurePanel className="text-sm lg:text-xs">
                <div
                  dangerouslySetInnerHTML={{
                    __html: productSheet.productDescription?.text,
                  }}
                />
                {productSheet.productDescription.hasModal && (
                  <Tooltip placement="top" message={<span dangerouslySetInnerHTML={{ __html: productSheet.productDescription.modalText }}></span>}>
                    <LabAddIcon />
                  </Tooltip>
                )}
              </DisclosurePanel>
            </Disclosure> */}

            {productSheet.care && productSheet.careTextArray.some((text) => !isBlank(text)) && !productSheet.recycled ?
              <Disclosure id="product-care">
                <DisclosureHeader level={2}>{t('ficha.producto', 'shoe.care', 'Product Care')}</DisclosureHeader>
                <DisclosurePanel className="text-sm lg:text-xs">
                  {productSheet.careTextArray && productSheet.careTextArray.length > 0 && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: productSheet.careTextArray[0],
                      }}
                    />
                  )}
                  <br />
                  {productSheet.careDetailsArray &&
                    productSheet.careDetailsArray.length > 0 &&
                    productSheet.careDetailsArray.map((careDetail, index) => (
                      <div
                        key={index}
                        dangerouslySetInnerHTML={{
                          __html: careDetail,
                        }}
                      />
                    ))}
                  {productSheet.careTextArray && productSheet.careTextArray.length > 1 && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: productSheet.careTextArray[1],
                      }}
                    />
                  )}
                </DisclosurePanel>
              </Disclosure>
            : null}
            {productSheet?.productSheetInfo?.map((section, index) => (
              <Disclosure id={section.title} key={`product-sheet-info-${section.title}-${index}`}>
                <DisclosureHeader level={2}>{section.title}</DisclosureHeader>
                <DisclosurePanel className="text-sm lg:text-xs">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: section.content,
                    }}
                  />
                </DisclosurePanel>
              </Disclosure>
            ))}
            {Array.isArray(productSheet.productMeasures) && productSheet.productMeasures.length > 0 ?
              <Disclosure id="gafas">
                <DisclosureHeader level={2}>{productSheet.productPartsSizing}</DisclosureHeader>
                <DisclosurePanel className="text-sm lg:text-xs">
                  <div className="flex flex-col gap-2">
                    {productSheet.productMeasures.map((measure) => (
                      <div key={`product-measure-${measure.img}`} className="grid grid-cols-[60px_1fr] gap-3">
                        <picture className="block h-full w-full">
                          <source srcSet={`${measureImgSrc}/${measure.img}.webp`} type="image/webp" />
                          <img height={60} width={60} src={`${measureImgSrc}/${measure.img}.png`} alt="" />
                        </picture>
                        <div className="flex flex-col justify-center gap-0.5 font-primary text-sm">
                          <p className="mb-0 font-moderne font-medium">{measure.titulo}</p>
                          <p className="mb-0">{measure.medida}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </DisclosurePanel>
              </Disclosure>
            : null}
            {!isBlank(productSheet?.productProcessingText) ?
              <Disclosure id="productProcessing" key="product-sheet-processing-text">
                <DisclosureHeader level={2}>{productSheet?.productProcessing}</DisclosureHeader>
                <DisclosurePanel className="text-sm lg:text-xs">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: productSheet?.productProcessingText,
                    }}
                  />
                </DisclosurePanel>
              </Disclosure>
            : null}
          </DisclosureGroup>
        </>
      : null}
    </div>
  );
}
